import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Package, Stethoscope, Plus, Pencil, X, Trash2, User, Building2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { formatCurrency, cn } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import { getErrorMessage } from '@/lib/errors';

type Tab = 'servicios' | 'productos';

const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';

const emptyForm = { nombre: '', precio: '', descripcion: '', medicoId: '', costo: '', stock: '0', stockMinimo: '0' };

export function ProductosPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();
  const canEdit = [ROL.SUPER_ADMIN, ROL.ADMIN_CLINICA, ROL.MEDICO_ADMIN].includes(usuario?.rolId as any);

  const [tab,     setTab]     = useState<Tab>('servicios');
  const [open,    setOpen]    = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form,    setForm]    = useState(emptyForm);

  // Médicos de la clínica para el selector de asignación
  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos'],
    queryFn: () => api.get('/api/medicos').then(r => r.data as any[]),
    staleTime: 120_000,
  });

  // Servicios
  const { data: servicios = [], isLoading: svcLoading } = useQuery({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data as any[]),
  });

  const createSvc = useMutation({
    mutationFn: (dto: any) => api.post('/api/servicios', dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['servicios'] }); toast.success('Servicio creado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear servicio')),
  });
  const updateSvc = useMutation({
    mutationFn: ({ id, dto }: any) => api.patch(`/api/servicios/${id}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['servicios'] }); toast.success('Servicio actualizado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar servicio')),
  });
  const deleteSvc = useMutation({
    mutationFn: (id: string) => api.patch(`/api/servicios/${id}`, { activo: false }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['servicios'] }); toast.success('Servicio eliminado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar servicio')),
  });

  // Productos
  const { data: productos = [], isLoading: prdLoading } = useQuery({
    queryKey: ['productos'],
    queryFn: () => api.get('/api/productos').then(r => r.data as any[]),
  });

  const createPrd = useMutation({
    mutationFn: (dto: any) => api.post('/api/productos', dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['productos'] }); toast.success('Producto creado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear producto')),
  });
  const updatePrd = useMutation({
    mutationFn: ({ id, dto }: any) => api.patch(`/api/productos/${id}`, dto).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['productos'] }); toast.success('Producto actualizado'); closeModal(); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar producto')),
  });
  const deletePrd = useMutation({
    mutationFn: (id: string) => api.patch(`/api/productos/${id}`, { activo: false }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['productos'] }); toast.success('Producto eliminado'); },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar producto')),
  });

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  }

  function openEdit(item: any) {
    setEditing(item);
    setForm({
      nombre:    item.nombre || '',
      precio:    item.precio?.toString() || '',
      descripcion: item.descripcion || '',
      medicoId:  item.medicoId || '',
      costo:     item.costo?.toString() || '',
      stock:     item.stock?.toString() ?? '0',
      stockMinimo: item.stockMinimo?.toString() ?? '0',
    });
    setOpen(true);
  }

  function closeModal() { setOpen(false); setEditing(null); }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const dto: any = {
      nombre:    form.nombre.trim(),
      precio:    parseFloat(form.precio),
      descripcion: form.descripcion.trim() || null,
      medicoId:  form.medicoId || null,
    };
    if (tab === 'productos') {
      dto.costo = form.costo ? parseFloat(form.costo) : null;
      dto.stock = parseInt(form.stock) || 0;
      dto.stockMinimo = parseInt(form.stockMinimo) || 0;
    }
    if (tab === 'servicios') {
      if (editing) updateSvc.mutate({ id: editing.id, dto });
      else createSvc.mutate(dto);
    } else {
      if (editing) updatePrd.mutate({ id: editing.id, dto });
      else createPrd.mutate(dto);
    }
  }

  function getMedicoNombre(medicoId: string) {
    const m = medicos.find((m: any) => m.id === medicoId);
    if (!m) return 'Médico';
    return [m.nombre, m.apellidoPaterno].filter(Boolean).join(' ');
  }

  const isPending = createSvc.isPending || updateSvc.isPending || createPrd.isPending || updatePrd.isPending;

  const tabs = [
    { id: 'servicios' as Tab, label: 'Servicios', icon: <Stethoscope size={15} /> },
    { id: 'productos' as Tab, label: 'Productos',  icon: <Package size={15} />     },
  ];

  const isLoading = tab === 'servicios' ? svcLoading : prdLoading;
  const items     = tab === 'servicios' ? servicios  : productos;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl font-bold text-text-primary">Productos y Servicios</h2>
        {canEdit && (
          <button onClick={openCreate} className="hidden md:flex items-center gap-2 bg-primary-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors">
            <Plus size={15} /> {tab === 'servicios' ? 'Nuevo servicio' : 'Nuevo producto'}
          </button>
        )}
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={cn('flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
              tab === t.id ? 'bg-white text-primary-700 shadow-sm' : 'text-text-secondary hover:text-text-primary')}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100">
        <div className="px-5 py-3 border-b border-gray-50">
          <p className="text-sm text-text-secondary">
            {items.length} {tab === 'servicios' ? `servicio${items.length !== 1 ? 's' : ''}` : `producto${items.length !== 1 ? 's' : ''}`}
          </p>
        </div>

        {isLoading ? (
          <p className="text-center py-8 text-text-secondary text-sm">Cargando...</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-text-secondary text-sm">Sin {tab === 'servicios' ? 'servicios' : 'productos'} registrados</p>
            {canEdit && (
              <button onClick={openCreate} className="mt-3 text-primary-600 text-sm font-medium hover:text-primary-700 transition-colors">
                + Crear {tab === 'servicios' ? 'servicio' : 'producto'}
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {items.map((item: any) => (
              <div key={item.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-text-primary">{item.nombre}</p>
                    {item.medicoId ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-100">
                        <User size={9} /> {getMedicoNombre(item.medicoId)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-100">
                        <Building2 size={9} /> Clínica
                      </span>
                    )}
                  </div>
                  {item.descripcion && (
                    <p className="text-xs text-text-secondary mt-0.5 truncate">{item.descripcion}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {tab === 'productos' && (
                    <span className={cn(
                      'text-[10px] font-medium px-2 py-0.5 rounded-full border',
                      item.stock === 0 ? 'bg-red-50 text-red-600 border-red-200' :
                      item.stock <= (item.stockMinimo || 0) ? 'bg-amber-50 text-amber-700 border-amber-200' :
                      'bg-gray-50 text-text-secondary border-gray-200'
                    )}>
                      {item.stock === 0 ? <><AlertTriangle size={9} className="inline mr-0.5"/>Sin stock</> : `Stock: ${item.stock}`}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-primary-600">{formatCurrency(item.precio)}</span>
                  {canEdit && (
                    <>
                      <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:bg-gray-100 text-text-secondary transition-colors">
                        <Pencil size={13} />
                      </button>
                      <button
                        onClick={() => tab === 'servicios' ? deleteSvc.mutate(item.id) : deletePrd.mutate(item.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-text-secondary hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={13} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FAB mobile */}
      {canEdit && (
        <button
          onClick={openCreate}
          className="md:hidden fixed bottom-20 right-4 z-30 w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center"
          style={{ boxShadow: '0 4px 16px rgba(14,133,191,0.4)' }}
          aria-label={tab === 'servicios' ? 'Nuevo servicio' : 'Nuevo producto'}
        >
          <Plus size={24} strokeWidth={2.5} />
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex flex-col justify-end md:justify-center md:items-center md:p-4">
          <div className="bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-sm max-h-[90vh] overflow-y-auto">
            <div className="flex justify-center pt-3 pb-1 md:hidden">
              <div className="w-10 h-1 rounded-full bg-gray-200" />
            </div>
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-text-primary">
                {editing ? 'Editar' : 'Nuevo'} {tab === 'servicios' ? 'servicio' : 'producto'}
              </h3>
              <button onClick={closeModal} className="p-1 rounded-lg hover:bg-gray-100 text-text-secondary">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Nombre *</label>
                <input required className={inputCls} value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  placeholder={tab === 'servicios' ? 'Consulta general' : 'Guantes de látex'} />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Precio (MXN) *</label>
                <input required type="number" min={0} step="0.01" className={inputCls}
                  value={form.precio} onChange={e => setForm(f => ({ ...f, precio: e.target.value }))}
                  placeholder="500.00" />
              </div>
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">Descripción</label>
                <textarea rows={2} className={`${inputCls} resize-none`}
                  value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))}
                  placeholder="Descripción opcional..." />
              </div>
              {tab === 'productos' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Costo (MXN)</label>
                      <input type="number" min={0} step="0.01" className={inputCls}
                        value={form.costo} onChange={e => setForm(f => ({ ...f, costo: e.target.value }))}
                        placeholder="0.00" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Stock</label>
                      <input type="number" min={0} step="1" className={inputCls}
                        value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))}
                        placeholder="0" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-text-secondary mb-1">Stock mín.</label>
                      <input type="number" min={0} step="1" className={inputCls}
                        value={form.stockMinimo} onChange={e => setForm(f => ({ ...f, stockMinimo: e.target.value }))}
                        placeholder="0" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="block text-xs font-medium text-text-secondary mb-1">
                  Asignar a médico <span className="font-normal">(opcional — si no se selecciona, aplica a toda la clínica)</span>
                </label>
                <select className={inputCls} value={form.medicoId}
                  onChange={e => setForm(f => ({ ...f, medicoId: e.target.value }))}>
                  <option value="">— Toda la clínica —</option>
                  {medicos.map((m: any) => (
                    <option key={m.id} value={m.id}>
                      {[m.nombre, m.apellidoPaterno, m.apellidoMaterno].filter(Boolean).join(' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeModal}
                  className="flex-1 border border-gray-200 text-text-secondary py-2 rounded-lg text-sm hover:bg-gray-50">
                  Cancelar
                </button>
                <button type="submit" disabled={isPending}
                  className="flex-1 bg-primary-500 text-white py-2 rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-60">
                  {editing ? 'Guardar cambios' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
